import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useRBAC } from '../../contexts/RBACContext';
import { useIsMobileNav } from '../../hooks/useMediaQuery';
import ClickUpNavTree from './ClickUpNavTree';
import {
  CLICKUP_TREE_PERMISSIONS,
  NAV_GROUPS,
  findNavLocation,
  isNavItemVisible,
  type NavGroup,
  type NavItem,
} from './navigation';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const readStoredCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const storeCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Storage may be unavailable (private mode) — the in-memory state still applies.
  }
};

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40';

interface SidebarLinkProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ item, active, collapsed }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.name : undefined}
      title={collapsed ? item.name : undefined}
      className={cn(
        'group flex items-center h-8 rounded-md text-sm transition-colors',
        focusRing,
        collapsed ? 'w-10 mx-auto justify-center' : 'gap-2.5 px-2.5',
        active
          ? 'bg-indigo-50 text-indigo-700 font-medium'
          : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900',
      )}
    >
      <Icon
        className={cn(
          'w-4 h-4 shrink-0 transition-colors',
          active ? 'text-indigo-600' : 'text-slate-500 group-hover:text-slate-700',
        )}
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{item.name}</span>}
      {!collapsed && item.badge && (
        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-100 px-1.5 text-xs font-semibold text-indigo-700 tabular-nums">
          {item.badge}
        </span>
      )}
    </Link>
  );
};

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface VisibleGroup {
  group: NavGroup;
  items: NavItem[];
  showClickUpTree: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed: controlledCollapsed,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) => {
  const { isLoading, hasAllPermissions, hasAnyPermission } = useRBAC();
  const location = useLocation();
  // On mobile the sidebar is a drawer, so the icons-only layout never applies.
  const isMobile = useIsMobileNav();

  const [internalCollapsed, setInternalCollapsed] = useState(readStoredCollapsed);
  const storedCollapsed = controlledCollapsed ?? internalCollapsed;
  const isCollapsed = isMobile ? false : storedCollapsed;

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    storeCollapsed(next);
    if (onToggleCollapse) onToggleCollapse(next);
    else setInternalCollapsed(next);
  };

  // Escape dismisses the open drawer.
  useEffect(() => {
    if (!mobileOpen || !onMobileClose) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMobileClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onMobileClose]);

  // Exactly one item is highlighted: the most specific match for the route.
  const activeHref = findNavLocation(location.pathname)?.item.href ?? null;

  // Filter before rendering so groups with nothing visible disappear entirely.
  const visibleGroups = useMemo<VisibleGroup[]>(() => {
    const rbac = { isLoading, hasAllPermissions, hasAnyPermission };
    const canSeeClickUpTree = isNavItemVisible({ permissions: CLICKUP_TREE_PERMISSIONS }, rbac);
    return NAV_GROUPS
      .map(group => ({
        group,
        items: group.items.filter(item => isNavItemVisible(item, rbac)),
        // The tree needs room to expand, so it only exists in the full-width layout.
        showClickUpTree: Boolean(group.includesClickUpTree) && canSeeClickUpTree && !isCollapsed,
      }))
      .filter(({ items, showClickUpTree }) => items.length > 0 || showClickUpTree);
  }, [isLoading, hasAllPermissions, hasAnyPermission, isCollapsed]);

  return (
    <>
      {mobileOpen && isMobile && (
        <div className="sidebar-mobile-overlay" onClick={onMobileClose} aria-hidden="true" data-testid="sidebar-mobile-overlay" />
      )}
      <aside
        className={cn('sidebar', isCollapsed && 'sidebar-collapsed', mobileOpen && 'sidebar-mobile-open')}
        aria-label="Sidebar"
        data-mobile-open={mobileOpen && isMobile ? 'true' : undefined}
      >
        <div className={cn(
          'relative flex h-14 shrink-0 items-center border-b border-slate-200',
          isCollapsed ? 'justify-center' : 'gap-2.5 px-4',
        )}>
          <Link to="/" className={cn('flex items-center gap-2.5 rounded-md', focusRing)} aria-label="Fulcrum home">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-600">
              <LayoutDashboard className="h-4 w-4 text-white" aria-hidden="true" />
            </span>
            {!isCollapsed && (
              <span className="text-[15px] font-semibold leading-5 tracking-tight text-slate-900">Fulcrum</span>
            )}
          </Link>
          {!isCollapsed && (
            <span className={cn(
              'rounded bg-slate-100 px-1.5 py-px text-[11px] font-medium leading-4 text-slate-500',
              'ml-auto',
            )}>
              Tom&amp;Co
            </span>
          )}
          {onMobileClose && (
            <button
              type="button"
              onClick={onMobileClose}
              className={cn(
                'md:hidden flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800',
                focusRing,
              )}
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <nav aria-label="Main" className={cn('flex-1 overflow-y-auto py-4', isCollapsed ? 'px-2' : 'px-3')}>
          <div className={cn('flex flex-col', isCollapsed ? 'gap-2' : 'gap-5')}>
            {visibleGroups.map(({ group, items, showClickUpTree }, index) => (
              <div key={group.id} role="group" aria-label={group.name} className="flex flex-col gap-0.5">
                {isCollapsed ? (
                  index > 0 && <div className="mx-2 mb-1.5 h-px bg-slate-100" aria-hidden="true" />
                ) : (
                  // A div, not a heading: sidebar labels must not join every page's h1/h2 outline.
                  <div className="px-2.5 pb-1.5 text-xs font-medium leading-4 text-slate-500" aria-hidden="true">
                    {group.name}
                  </div>
                )}
                {items.map(item => (
                  <SidebarLink
                    key={item.href}
                    item={item}
                    active={item.href === activeHref}
                    collapsed={isCollapsed}
                  />
                ))}
                {showClickUpTree && <ClickUpNavTree />}
              </div>
            ))}
          </div>
        </nav>

        <div className="hidden shrink-0 border-t border-slate-200 p-3 md:block">
          <button
            type="button"
            onClick={handleToggleCollapse}
            className={cn(
              'flex h-8 w-full items-center rounded-md text-[13px] text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700',
              focusRing,
              isCollapsed ? 'justify-center' : 'gap-2.5 px-2.5',
            )}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : undefined}
          >
            {isCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                Collapse sidebar
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
